import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy, CreditCard, List, Lock, MessageSquare, Shield, Code, BarChart, Terminal } from 'lucide-react';
import { DocsPageHeader } from '../../../components/docs/DocsPageHeader';

const codeBlockClass = 'overflow-x-auto whitespace-pre-wrap break-words rounded-2xl bg-zinc-950 border border-zinc-800 p-5 text-sm font-mono text-zinc-200 antialiased leading-relaxed shadow-lg';

const MODEL_ID_PLACEHOLDER = 'your-model-id';

export function ApiOverviewContent() {
  const { t } = useTranslation('docs');
  const coreApis = [
    {
      category: t('sidebar.chat_completions'),
      icon: MessageSquare,
      color: 'from-blue-500 to-indigo-600',
      endpoints: [
        { method: 'POST', path: '/v1/chat/completions', description: t('api.send_chat') },
      ]
    },
    {
      category: t('sidebar.responses'),
      icon: Code,
      color: 'from-orange-500 to-amber-600',
      endpoints: [
        { method: 'POST', path: '/v1/responses', description: t('api.send_responses') },
      ]
    },
    {
      category: t('sidebar.messages'),
      icon: Terminal,
      color: 'from-rose-500 to-red-600',
      endpoints: [
        { method: 'POST', path: '/v1/messages', description: t('api.send_messages') },
      ]
    },
    {
      category: t('sidebar.models'),
      icon: List,
      color: 'from-emerald-500 to-teal-600',
      endpoints: [
        { method: 'GET', path: '/v1/models', description: t('api.list_models') },
      ]
    },
    {
      category: t('sidebar.usage'),
      icon: BarChart,
      color: 'from-purple-500 to-pink-600',
      endpoints: [
        { method: 'GET', path: '/v1/usage', description: t('api_usage.endpoint_desc') },
      ]
    }
  ];

  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api.badge')}
        title={t('api.heading')}
        description={t('api.description')}
      />

      {/* Base Configuration */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('api.base_config')}</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{t('api.base_url')}</span>
              <code className="bg-muted px-3 py-1 rounded-lg text-sm font-mono text-primary">https://api.etherworks.cn</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{t('api.content_type')}</span>
              <code className="bg-muted px-3 py-1 rounded-lg text-sm font-mono">application/json</code>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{t('api.authorization')}</span>
              <code className="bg-muted px-3 py-1 rounded-lg text-sm font-mono">Bearer YOUR_API_KEY</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{t('api.timeout')}</span>
              <code className="bg-muted px-3 py-1 rounded-lg text-sm font-mono">{t('api.timeout_value')}</code>
            </div>
          </div>
        </div>
      </div>

      {/* Core APIs Grid */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-foreground">{t('api.core_endpoints')}</h3>
        <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
          {coreApis.map(({ category, icon: Icon, color, endpoints }) => (
            <div key={category} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
              <div className={`bg-gradient-to-r ${color} px-5 py-4`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white">{category}</h4>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {endpoints.map(({ method, path, description }) => (
                  <div key={path} className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      method === 'GET' ? 'bg-green-500/10 text-green-500' :
                      method === 'POST' ? 'bg-primary/10 text-primary' :
                      method === 'PATCH' ? 'bg-amber-500/10 text-amber-500' :
                      method === 'DELETE' ? 'bg-red-500/10 text-red-500' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {method}
                    </span>
                    <div className="flex-1 min-w-0">
                      <code className="text-sm font-mono text-foreground break-all">{path}</code>
                      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Example */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api.quick_example')}</h3>
        <pre className={codeBlockClass}>
          {`curl -X POST https://api.etherworks.cn/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${MODEL_ID_PLACEHOLDER}",
    "messages": [{"role": "user", "content": "Hello, how are you?"}]
  }'`}
        </pre>
      </div>
    </div>
  );
}

export function ApiChatCompletionsContent() {
  const { t } = useTranslation('docs');
  const [copied, setCopied] = useState('');
  
  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const chatExample = `{
  "model": "${MODEL_ID_PLACEHOLDER}",
  "messages": [
    {"role": "system", "content": "You are a helpful assistant."},
    {"role": "user", "content": "What is the capital of France?"}
  ],
  "temperature": 0.7,
  "stream": false
}`;

  const responseExample = `{
  "id": "chatcmpl-123",
  "object": "chat.completion",
  "created": 1699564800,
  "model": "${MODEL_ID_PLACEHOLDER}",
  "choices": [{
    "index": 0,
    "message": {
      "role": "assistant",
      "content": "The capital of France is Paris."
    },
    "finish_reason": "stop"
  }],
  "usage": {
    "prompt_tokens": 25,
    "completion_tokens": 7,
    "total_tokens": 32
  }
}`;

  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api_chat.badge')}
        title={t('api_chat.heading')}
        description={t('api_chat.description')}
      />

      {/* Endpoint */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-bold">POST</span>
          <code className="text-lg font-mono text-foreground">/v1/chat/completions</code>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('api_chat.endpoint_desc')}
        </p>
      </div>

      {/* Request Parameters */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_params')}</h3>
        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.type')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.required')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param_description')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-3 font-mono text-primary">model</td>
                <td className="px-4 py-3 text-muted-foreground">string</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_chat.param_model')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">messages</td>
                <td className="px-4 py-3 text-muted-foreground">array</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_chat.param_messages')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">temperature</td>
                <td className="px-4 py-3 text-muted-foreground">number</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_chat.param_temperature')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">max_tokens</td>
                <td className="px-4 py-3 text-muted-foreground">integer</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_chat.param_max_tokens')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">stream</td>
                <td className="px-4 py-3 text-muted-foreground">boolean</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_chat.param_stream')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_example')}</h3>
          <button
            onClick={() => copyCode(chatExample, 'chat-req')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'chat-req' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'chat-req' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{chatExample}</pre>
      </div>

      {/* Response Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.response_example')}</h3>
          <button
            onClick={() => copyCode(responseExample, 'chat-res')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'chat-res' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'chat-res' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{responseExample}</pre>
      </div>
    </div>
  );
}

export function ApiResponsesContent() {
  const { t } = useTranslation('docs');
  const [copied, setCopied] = useState('');

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const requestExample = `{
  "model": "${MODEL_ID_PLACEHOLDER}",
  "instructions": "You are a coding assistant.",
  "input": "Write a haiku about recursion in programming.",
  "stream": false
}`;

  const responseExample = `{
  "id": "resp_abc123",
  "object": "response",
  "created_at": 1699564800,
  "status": "completed",
  "model": "${MODEL_ID_PLACEHOLDER}",
  "output": [
    {
      "type": "message",
      "id": "msg_abc123",
      "role": "assistant",
      "status": "completed",
      "content": [
        { "type": "output_text", "text": "Recursive calls dive / base case whispers softly back / stack unwinds to peace", "annotations": [] }
      ]
    }
  ],
  "usage": {
    "input_tokens": 25,
    "input_tokens_details": { "cached_tokens": 0 },
    "output_tokens": 18,
    "output_tokens_details": { "reasoning_tokens": 0 },
    "total_tokens": 43
  }
}`;

  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api_responses.badge')}
        title={t('api_responses.heading')}
        description={t('api_responses.description')}
      />

      {/* Endpoint */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-bold">POST</span>
          <code className="text-lg font-mono text-foreground">/v1/responses</code>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('api_responses.endpoint_desc')}
        </p>
      </div>

      {/* Request Parameters */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_params')}</h3>
        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.type')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.required')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param_description')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-3 font-mono text-primary">model</td>
                <td className="px-4 py-3 text-muted-foreground">string</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_model')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">input</td>
                <td className="px-4 py-3 text-muted-foreground">string | array</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_input')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">instructions</td>
                <td className="px-4 py-3 text-muted-foreground">string</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_instructions')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">tools</td>
                <td className="px-4 py-3 text-muted-foreground">array</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_tools')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">max_output_tokens</td>
                <td className="px-4 py-3 text-muted-foreground">integer</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_max_output_tokens')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">stream</td>
                <td className="px-4 py-3 text-muted-foreground">boolean</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_responses.param_stream')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_example')}</h3>
          <button
            onClick={() => copyCode(requestExample, 'resp-req')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'resp-req' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'resp-req' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{requestExample}</pre>
      </div>

      {/* Response Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.response_example')}</h3>
          <button
            onClick={() => copyCode(responseExample, 'resp-res')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'resp-res' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'resp-res' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{responseExample}</pre>
      </div>
    </div>
  );
}

export function ApiMessagesContent() {
  const { t } = useTranslation('docs');
  const [copied, setCopied] = useState('');

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const requestExample = `{
  "model": "${MODEL_ID_PLACEHOLDER}",
  "max_tokens": 1024,
  "system": "You are a coding assistant.",
  "messages": [
    { "role": "user", "content": "What is the capital of France?" }
  ]
}`;

  const responseExample = `{
  "id": "msg_abc123",
  "type": "message",
  "role": "assistant",
  "model": "${MODEL_ID_PLACEHOLDER}",
  "content": [
    { "type": "text", "text": "The capital of France is Paris." }
  ],
  "stop_reason": "end_turn",
  "stop_sequence": null,
  "usage": {
    "input_tokens": 12,
    "output_tokens": 8
  }
}`;

  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api_messages.badge')}
        title={t('api_messages.heading')}
        description={t('api_messages.description')}
      />

      {/* Endpoint */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-bold">POST</span>
          <code className="text-lg font-mono text-foreground">/v1/messages</code>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('api_messages.endpoint_desc')}
        </p>
      </div>

      {/* Request Parameters */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_params')}</h3>
        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.type')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.required')}</th>
                <th className="px-4 py-3 text-left font-semibold text-foreground">{t('api_chat.param_description')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-3 font-mono text-primary">model</td>
                <td className="px-4 py-3 text-muted-foreground">string</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_model')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">messages</td>
                <td className="px-4 py-3 text-muted-foreground">array</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_messages')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">max_tokens</td>
                <td className="px-4 py-3 text-muted-foreground">integer</td>
                <td className="px-4 py-3"><span className="text-green-500 font-medium">{t('api_chat.yes')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_max_tokens')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">system</td>
                <td className="px-4 py-3 text-muted-foreground">string</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_system')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">tools</td>
                <td className="px-4 py-3 text-muted-foreground">array</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_tools')}</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-primary">stream</td>
                <td className="px-4 py-3 text-muted-foreground">boolean</td>
                <td className="px-4 py-3"><span className="text-muted-foreground">{t('api_chat.no')}</span></td>
                <td className="px-4 py-3 text-muted-foreground">{t('api_messages.param_stream')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.request_example')}</h3>
          <button
            onClick={() => copyCode(requestExample, 'msg-req')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'msg-req' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'msg-req' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{requestExample}</pre>
      </div>

      {/* Response Example */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">{t('api_chat.response_example')}</h3>
          <button
            onClick={() => copyCode(responseExample, 'msg-res')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground bg-muted hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied === 'msg-res' ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            {copied === 'msg-res' ? t('api_chat.copied') : t('api_chat.copy')}
          </button>
        </div>
        <pre className={codeBlockClass}>{responseExample}</pre>
      </div>
    </div>
  );
}

export function ApiModelsContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api_models.badge')}
        title={t('api_models.heading')}
        description={t('api_models.description')}
      />

      {/* Endpoint */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-green-500/10 text-green-500 rounded-lg text-sm font-bold">GET</span>
          <code className="text-lg font-mono text-foreground">/v1/models</code>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('api_models.list_desc')}
        </p>
      </div>

      {/* API Compatibility */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_models.endpoints')}</h3>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <p className="text-sm text-muted-foreground mb-4">
            {t('api_models.config_note')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="inline-block px-2 py-1 rounded text-xs font-bold bg-green-500/10 text-green-500 mb-2">{t('api_models.chat_completions_badge')}</span>
              <p className="text-sm text-muted-foreground">/v1/chat/completions</p>
            </div>
            <div>
              <span className="inline-block px-2 py-1 rounded text-xs font-bold bg-primary/10 text-primary mb-2">{t('api_models.models_badge')}</span>
              <p className="text-sm text-muted-foreground">/v1/models</p>
            </div>
          </div>
        </div>
      </div>

      {/* Response Example */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_chat.response_example')}</h3>
        <pre className={codeBlockClass}>
          {`{
  "object": "list",
  "data": [
    {
      "id": "${MODEL_ID_PLACEHOLDER}",
      "object": "model",
      "created": 1718841600,
      "owned_by": "system"
    }
  ]
}`}
        </pre>
      </div>
    </div>
  );
}

export function ApiUsageContent() {
  const { t } = useTranslation('docs');
  
  return (
    <div className="space-y-10">
      <DocsPageHeader
        badge={t('api_usage.badge')}
        title={t('api_usage.heading')}
        description={t('api_usage.description')}
      />

      {/* Endpoint */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-3 py-1 bg-green-500/10 text-green-500 rounded-lg text-sm font-bold">GET</span>
          <code className="text-lg font-mono text-foreground">/v1/usage</code>
        </div>
        <p className="text-sm text-muted-foreground">
          {t('api_usage.endpoint_desc')}
        </p>
      </div>

      {/* Response Example */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">{t('api_usage.response_example')}</h3>
        <pre className={codeBlockClass}>
          {`{
  "object": "list",
  "data": [
    {
      "object": "usage",
      "timestamp": 1718841600,
      "aggregation_type": "day",
      "total_tokens": 1250,
      "total_requests": 15
    }
  ]
}`}
        </pre>
        <p className="text-sm text-muted-foreground">
          {t('api_usage.response_note')}
        </p>
      </div>
    </div>
  );
}

export function ApiStreamingContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-6">
      <DocsPageHeader
        badge={t('api.badge')}
        title={t('api_streaming.heading')}
        description={t('api_streaming.description')}
      />
      <pre className={codeBlockClass}>
        {`const controller = new AbortController();
const res = await fetch('https://api.etherworks.cn/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + process.env.APP_API_KEY,
  },
  body: JSON.stringify({
    model: '${MODEL_ID_PLACEHOLDER}',
    messages: [{ role: 'user', content: 'Hello!' }],
    stream: true,
  }),
  signal: controller.signal,
});

const reader = res.body?.getReader();
while (true) {
  const { value, done } = await reader.read();
  if (done) break;
  console.log(new TextDecoder().decode(value));
}
`}
      </pre>
      <p className="text-xs text-muted-foreground">
        {t('api_streaming.note')}
      </p>
    </div>
  );
}

export function ApiErrorsContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-6">
      <DocsPageHeader
        badge={t('api.badge')}
        title={t('api_errors.heading')}
        description={t('api_errors.description')}
      />
      <pre className={codeBlockClass}>
        {`{
  "error": {
    "message": "The model '${MODEL_ID_PLACEHOLDER}' does not exist or you do not have access to it.",
    "type": "invalid_request_error",
    "param": null,
    "code": "model_not_found"
  }
}
`}
      </pre>
      <p className="text-xs text-muted-foreground">
        {t('api_errors.note')}
      </p>
    </div>
  );
}
